/**
 * Booking status constants
 * These values match the database schema and GraphQL types
 * Booking status is stored as a number in the database:
 * - 0: Pending
 * - 1: Confirmed
 * - 2: Cancelled
 */

/**
 * Booking status mapping
 * Maps database status number values to display labels and colors
 *
 * @example
 * ```ts
 * const status = getBookingStatus(1);
 * // Returns: { label: 'Confirmed', color: 'bg-green-100 text-green-800' }
 * ```
 */
export const BOOKING_STATUS_MAP = {
  0: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  1: { label: 'Confirmed', color: 'bg-green-100 text-green-800' },
  2: { label: 'Cancelled', color: 'bg-red-100 text-red-800' },
} as const;

export type BookingStatus = keyof typeof BOOKING_STATUS_MAP;

/**
 * Status configuration type
 */
export type BookingStatusConfig =
  (typeof BOOKING_STATUS_MAP)[keyof typeof BOOKING_STATUS_MAP];

/**
 * Get booking status configuration
 * Returns the status configuration for a given status number, or defaults to pending (0)
 *
 * @param status - The booking status number from the database/GraphQL
 * @returns Status configuration object with label and color
 *
 * @example
 * ```ts
 * const status = getBookingStatus(1);
 * // Returns: { label: 'Confirmed', color: 'bg-green-100 text-green-800' }
 * ```
 */
export const getBookingStatus = (status: number): BookingStatusConfig => {
  return BOOKING_STATUS_MAP[status as BookingStatus] || BOOKING_STATUS_MAP[0];
};

/**
 * Type guard to check if a number is a valid booking status
 *
 * @param status - The number to validate
 * @returns True if the number is a valid booking status
 *
 * @example
 * ```ts
 * if (isValidBookingStatus(1)) {
 *   // TypeScript knows status is BookingStatus here
 * }
 * ```
 */
export const isValidBookingStatus = (
  status: number,
): status is BookingStatus => {
  return status in BOOKING_STATUS_MAP;
};

/**
 * Array of all valid booking statuses
 * Useful for form selects or iteration
 */
export const BOOKING_STATUSES = Object.keys(BOOKING_STATUS_MAP).map(
  Number,
) as BookingStatus[];

/**
 * Booking status display order
 * Defines the order in which statuses should be displayed in UI
 */
export const BOOKING_STATUS_ORDER: BookingStatus[] = [0, 1, 2];

/**
 * Booking status enum values for easier reference
 */
export const BOOKING_STATUS = {
  PENDING: 0,
  CONFIRMED: 1,
  CANCELLED: 2,
} as const;
